import { computed, ref } from 'vue'

import { useModal } from '@shared/composables/use-modal'
import { useSessionProvider } from '@shared/composables/use-session-provider'
import { Namespaces } from '@shared/models/enums/namespaces'
import { IAnnouncementNotification } from '@shared/models/proto/base/announcement'
import { IGetAnnouncementNotificationsRO } from '@shared/models/proto/ro/announcement-notifications'
import { useAnnouncementNotificationsStore } from '@shared/store/announcement-notifications'
import { useFetchData } from '@shared/utils/composables/fetch-data'
import { INITIAL_PER_PAGE_VARIANTS } from '@shared/utils/constants/pagination'
import { createSharedComposable } from '@shared/utils/helpers/create-composable'

export const useAnnouncementNotifications = createSharedComposable(
  (params: { namespace: Namespaces }) => {
    const { namespace } = params

    const { fetchData, isLoading, isSuccess } =
      useFetchData<IGetAnnouncementNotificationsRO>()

    const { isVisible, setVisible } = useModal()

    const { isAnnouncementEnabled } = useSessionProvider()

    const announcementsStore = useAnnouncementNotificationsStore(namespace)

    const pagination = ref({
      page: 1,
      perPage: INITIAL_PER_PAGE_VARIANTS[3],
    })

    const announcementList = computed(() => announcementsStore.announcementList)

    const meta = computed(() => announcementsStore.meta)

    const isDisabledScroll = computed(
      () => isLoading.value || meta.value?.totalPages === meta.value?.page
    )

    const createAnnouncementChannel = () => {
      if (isAnnouncementEnabled.value) {
        announcementsStore.createAnnouncementChannel()
      }
    }

    const readNotification = (notification: IAnnouncementNotification) =>
      announcementsStore.readNotification(notification)

    const scrollHandler = () => {
      pagination.value.page += 1
      fetchNotificationData()
    }

    const openDrawer = () => {
      setVisible(true)

      fetchNotificationData()
    }

    const fetchNotificationData = () => {
      fetchData({
        onFetchDataCallback: async () => {
          return await announcementsStore.fetchNotifications(
            pagination.value,
            isSuccess.value
          )
        },
      })
    }

    return {
      setVisible,
      openDrawer,
      fetchNotificationData,
      scrollHandler,
      readNotification,
      createAnnouncementChannel,
      isLoading,
      isVisible,
      isDisabledScroll,
      announcementList,
    }
  }
)
